import React, { useMemo, useState } from "react";
import { flattenHistoryAll, formatPrice, prettyAuction, STATUS_COLOR } from "./parseHistory.js";
import { Skel } from "./carHelpers.jsx";

/**
 * Sales History block — full-width section under the spec grid.
 *
 * Layout:
 *   ┌ title + meta ─────────────────────────────────────────┐
 *   │ BANNER                                                │
 *   │   Left: status + big price + date + auction           │
 *   │   Right: three divider-separated stats                │
 *   ├───────────────────────────────────────────────────────┤
 *   │ TABLE with timeline dots + current-lot chip           │
 *   │ FOOTER: counter text + single CTA                     │
 *   └───────────────────────────────────────────────────────┘
 */
export default function SalesHistoryBlock({ bidcars, ag, agMeta, queryLotNumber, auctionKey }) {
  const [showAllHistory, setShowAllHistory] = useState(false);
  const lotInfo = agMeta?.lotInfo ?? null;

  const currentLot =
    bidcars?.lotNumber ||
    lotInfo?.lotNumber ||
    queryLotNumber ||
    firstRow(ag?.historyAll)?.lot_number ||
    null;

  const rows = flattenHistoryAll(ag?.historyAll);
  const head = rows[0];

  const headlinePriceRaw = Number(head?.purchase_price) || Number(bidcars?.currentBid) || 0;
  const hasHeadlinePrice = headlinePriceRaw > 0;
  let headlineStatus = head?.status || "Active";
  const soldFlag = Boolean(bidcars?.sold);
  if (auctionKey?.includes("copart") && soldFlag) headlineStatus = "Sold";
  if (auctionKey?.includes("copart") && !soldFlag && !head) headlineStatus = "Active";

  const visibleRows = showAllHistory ? rows : rows.slice(0, 4);
  const restCount = Math.max(0, rows.length - 4);
  const saleDateStr = formatSaleDate(bidcars?.saleDate);
  const showActiveFallback = rows.length === 0 && currentLot;

  // Only the most recent record matching the active lot gets the "current" chip.
  const currentRowKey = useMemo(() => {
    if (!currentLot) return null;
    const match = rows.find((r) => String(r.lot_number) === String(currentLot));
    if (!match) return null;
    return match.id ?? `${match.lot_number}-${match.sale_date}`;
  }, [rows, currentLot]);

  const stats = useMemo(() => {
    if (rows.length === 0) return { total: null, highest: null, average: null };
    const prices = rows
      .map((r) => Number(r?.purchase_price))
      .filter((n) => Number.isFinite(n) && n > 0);
    const highest = prices.length ? Math.max(...prices) : null;
    const avg = prices.length
      ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length)
      : null;
    return { total: rows.length, highest, average: avg };
  }, [rows]);

  const lastAuction = head?.auction ? prettyAuction(head.auction) : null;
  const lastDate = head?.auctionDate || formatYYYYMMDD(head?.sale_date) || null;

  return (
    <section className="nd-car-detail__spec-block nd-car-detail__spec-block--full sales-history">
      <div className="sales-history__head">
        <h3 className="nd-car-detail__spec-block-title sales-history__title">Sales History</h3>
        <span className="small muted">Final bids · no auction fees</span>
      </div>

      <div className="sales-history__banner">
        <div className="sales-history__banner-main">
          <div className="sales-history__banner-top">
            <StatusPill status={headlineStatus} />
            <span className="sales-history__banner-sub">
              {hasHeadlinePrice ? "Last recorded sale" : "No final bids yet"}
            </span>
          </div>
          <div className="sales-history__banner-price">
            {hasHeadlinePrice ? `$${formatPrice(headlinePriceRaw)}` : <Skel w={180} />}
          </div>
          <div className="sales-history__banner-meta">
            {lastDate ? <b>{lastDate}</b> : currentLot ? <>Lot <b>{currentLot}</b></> : <Skel w={110} />}
            {lastAuction && <> · {lastAuction}</>}
            {!lastDate && currentLot && saleDateStr && <> · next auction {saleDateStr}</>}
          </div>
        </div>

        <div className="sales-history__stats">
          <Stat label="Records" value={stats.total != null ? stats.total : null} />
          <Stat
            label="Highest bid"
            value={stats.highest ? `$${formatPrice(stats.highest)}` : null}
          />
          <Stat
            label="Average"
            value={stats.average ? `$${formatPrice(stats.average)}` : null}
          />
        </div>
      </div>

      <div className="sales-history__table" role="table">
        <div className="sales-history__thead" role="row">
          <span></span>
          <span>Lot</span>
          <span>Auction</span>
          <span>Status</span>
          <span>Date</span>
          <span className="sales-history__th-amount">Amount</span>
        </div>

        {showActiveFallback && (
          <Row
            lot={currentLot}
            auction={prettyAuction(auctionKey)}
            status="Active"
            date={saleDateStr || "Future"}
            amount="—"
            isCurrent
          />
        )}

        {visibleRows.map((r, idx) => {
          const rowKey = r.id ?? `${r.lot_number}-${r.sale_date}`;
          return (
            <Row
              key={rowKey}
              lot={r.lot_number}
              auction={prettyAuction(r.auction)}
              status={r.status}
              date={r.auctionDate || formatYYYYMMDD(r.sale_date)}
              amount={r.purchase_price ? `$${formatPrice(r.purchase_price)}` : "—"}
              isCurrent={rowKey === currentRowKey}
              isLatest={idx === 0}
            />
          );
        })}

        {!showActiveFallback && rows.length === 0 && (
          <div className="sales-history__empty">
            <div className="sales-history__empty-icon">📋</div>
            <div className="sales-history__empty-title">No completed sales yet</div>
            <div className="sales-history__empty-sub">
              This lot hasn’t crossed the block — any past or future sales will appear here.
            </div>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="sales-history__footer">
          <span className="small muted">
            Showing <b>{Math.min(visibleRows.length, rows.length)}</b> of{" "}
            <b>{rows.length}</b> records
            {restCount > 0 && !showAllHistory && (
              <>
                {" — "}
                <button
                  type="button"
                  className="sales-history__inline-btn"
                  onClick={() => setShowAllHistory(true)}
                >
                  show all
                </button>
              </>
            )}
            {showAllHistory && rows.length > 4 && (
              <>
                {" — "}
                <button
                  type="button"
                  className="sales-history__inline-btn"
                  onClick={() => setShowAllHistory(false)}
                >
                  collapse
                </button>
              </>
            )}
          </span>
          <button type="button" className="btn btn-accent btn-sm sales-history__full-btn">
            FULL HISTORY →
          </button>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="sales-history__stat">
      <div className="sales-history__stat-label">{label}</div>
      <div className="sales-history__stat-value">
        {value == null ? <Skel w={50} /> : value}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const palette = STATUS_PILL[status] || STATUS_PILL.Default;
  return (
    <span
      className="sales-history__pill"
      style={{ background: palette.bg, color: palette.fg, borderColor: palette.border }}
    >
      <span className="sales-history__pill-dot" style={{ background: palette.fg }} />
      {status}
    </span>
  );
}

function Row({ lot, auction, status, date, amount, isCurrent, isLatest }) {
  return (
    <div className={`sales-history__row${isCurrent ? " is-current" : ""}`} role="row">
      <span className="sales-history__dot-cell" aria-hidden="true">
        <span className={`sales-history__dot${isLatest ? " is-latest" : ""}${isCurrent ? " is-current" : ""}`} />
      </span>
      <span className="sales-history__lot">
        {lot}
        {isCurrent && <span className="sales-history__current-chip">current</span>}
      </span>
      <span className="sales-history__auction">{auction}</span>
      <span style={{ color: STATUS_COLOR[status] || "inherit", fontWeight: 700 }}>{status}</span>
      <span className="sales-history__date">{date}</span>
      <span className="sales-history__amount">{amount}</span>
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────
function firstRow(historyAll) {
  if (!historyAll) return null;
  for (const bucket of Object.values(historyAll)) {
    const arr = Array.isArray(bucket) ? bucket : Object.values(bucket ?? {});
    if (arr.length) return arr[0];
  }
  return null;
}

function formatYYYYMMDD(n) {
  const s = String(n ?? "");
  if (!/^\d{8}$/.test(s)) return s || null;
  return `${s.slice(6, 8)}.${s.slice(4, 6)}.${s.slice(0, 4)}`;
}

function formatSaleDate(d) {
  if (!d) return null;
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return String(d);
  return date.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
}

const STATUS_PILL = {
  Active:       { bg: "#dcfce7", fg: "#15803d", border: "#bbf7d0" },
  Sold:         { bg: "#dbeafe", fg: "#1e3a8a", border: "#bfdbfe" },
  "Not sold":   { bg: "#fee2e2", fg: "#991b1b", border: "#fecaca" },
  Pending:      { bg: "#fef3c7", fg: "#92400e", border: "#fde68a" },
  "On Approval":{ bg: "#e5e7eb", fg: "#334155", border: "#cbd5e1" },
  Default:      { bg: "#f1f5f9", fg: "#475569", border: "#cbd5e1" },
};
